import { lazy, Suspense, Component, ComponentType } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { BRONZE_600 } from '@shared/brand-colors';
import { SkeletonCard, SkeletonText, LuxurySkeleton } from '@/components/ui/luxury-skeleton';

// Error boundary that catches chunk-load failures and render errors inside lazy pages
interface LazyErrorBoundaryState { hasError: boolean }
class LazyErrorBoundary extends Component<{ children: ReactNode }, LazyErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[LazyErrorBoundary] Failed to load page chunk:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
          <div style={{ textAlign: "center", fontFamily: "Bodoni Moda, Georgia, serif" }}>
            <p style={{ fontSize: "1.1rem", color: "#1a1a1a", marginBottom: "12px" }}>Failed to load this page</p>
            <p style={{ fontSize: "0.875rem", color: "#6b7280", marginBottom: "20px", fontFamily: "Montserrat, sans-serif" }}>
              This can happen after an update. Please refresh to continue.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{ padding: "10px 24px", background: BRONZE_600, color: "#fff", border: "none", borderRadius: "0px", fontSize: "0.9rem", cursor: "pointer", fontFamily: "Montserrat, sans-serif" }}
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Retry a dynamic import once; if it still fails (commonly a stale chunk after a
// new deploy), force a one-time hard reload to fetch fresh chunk hashes instead
// of leaving the user stuck on a "Failed to load this page" screen.
// Module-scoped guard so a blocked/unavailable sessionStorage can never cause a
// reload loop within a single page session.
let lazyChunkReloadAttempted = false;

function retryDynamicImport<T>(factory: () => Promise<T>): Promise<T> {
  const RELOAD_KEY = "lazyChunkReloaded";
  return factory()
    .then((mod) => {
      try { window.sessionStorage.removeItem(RELOAD_KEY); } catch { /* ignore */ }
      return mod;
    })
    .catch(() =>
      // Second attempt handles a transient network hiccup.
      factory().catch((retryError) => {
        let alreadyReloaded = lazyChunkReloadAttempted;
        try { alreadyReloaded = alreadyReloaded || window.sessionStorage.getItem(RELOAD_KEY) === "1"; } catch { /* ignore */ }
        if (!alreadyReloaded) {
          lazyChunkReloadAttempted = true;
          try { window.sessionStorage.setItem(RELOAD_KEY, "1"); } catch { /* ignore */ }
          window.location.reload();
          // Keep Suspense pending while the page reloads.
          return new Promise<T>(() => {});
        }
        throw retryError;
      })
    );
}

function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(() => retryDynamicImport(factory));
}

// Lazy loading wrapper with sophisticated loading states
interface LazyWrapperProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  delay?: number;
}

export function LazyWrapper({ 
  children, 
  fallback = <LuxurySkeleton className="h-64 w-full" />,
  delay = 200 
}: LazyWrapperProps) {
  return (
    <LazyErrorBoundary>
      <Suspense fallback={
        <div className="animate-pulse" style={{ animationDelay: `${delay}ms` }}>
          {fallback}
        </div>
      }>
        {children}
      </Suspense>
    </LazyErrorBoundary>
  );
}

// Lazy load admin components (heavy and rarely used)
export const AdminDashboard = lazyWithRetry(() => 
  import('@/pages/admin/admin-dashboard').then(module => ({ 
    default: module.default 
  }))
);

export const AdminBookings = lazyWithRetry(() => 
  import('@/pages/admin/admin-bookings').then(module => ({ 
    default: module.default 
  }))
);

export const AdminCalendar = lazyWithRetry(() => 
  import('@/pages/admin/admin-calendar').then(module => ({ 
    default: module.default 
  }))
);

export const AdminContentManagement = lazyWithRetry(() => 
  import('@/pages/admin/admin-content-management').then(module => ({ 
    default: module.default 
  }))
);

export const AdminReporting = lazyWithRetry(() => 
  import('@/pages/admin/admin-reporting').then(module => ({ 
    default: module.default 
  }))
);

export const AdminSettings = lazyWithRetry(() => 
  import('@/pages/admin/admin-settings').then(module => ({ 
    default: module.default 
  }))
);

export const AdminReviews = lazyWithRetry(() => 
  import('@/pages/admin/admin-reviews').then(module => ({ 
    default: module.default 
  }))
);

export const AdminCommunications = lazyWithRetry(() => 
  import('@/pages/admin/admin-communications').then(module => ({ 
    default: module.default 
  }))
);

export const AdminBlog = lazyWithRetry(() =>
  import('@/pages/admin/admin-blog').then(module => ({
    default: module.default
  }))
);

export const AdminBacklinks = lazyWithRetry(() =>
  import('@/pages/admin/admin-backlinks').then(module => ({
    default: module.default
  }))
);

export const AdminLinkOpportunities = lazyWithRetry(() =>
  import('@/pages/admin/admin-link-opportunities').then(module => ({
    default: module.default
  }))
);

export const AdminOutreach = lazyWithRetry(() =>
  import('@/pages/admin/admin-outreach').then(module => ({
    default: module.default
  }))
);

export const AdminUsers = lazyWithRetry(() =>
  import('@/pages/admin/admin-users').then(module => ({
    default: module.default
  }))
);

export const AdminProperties = lazyWithRetry(() =>
  import('@/pages/admin/admin-properties').then(module => ({
    default: module.default
  }))
);

export const AdminEmails = lazyWithRetry(() =>
  import('@/pages/admin/admin-emails').then(module => ({
    default: module.default
  }))
);

export const AdminLegal = lazyWithRetry(() =>
  import('@/pages/admin/admin-legal').then(module => ({
    default: module.default
  }))
);

// Lazy load secondary pages
export const StayPage = lazyWithRetry(() => 
  import('@/pages/stay-page').then(module => ({ 
    default: module.default 
  }))
);

export const ContactPage = lazyWithRetry(() => 
  import('@/pages/contact-page').then(module => ({ 
    default: module.default 
  }))
);

export const PoliciesPage = lazyWithRetry(() => 
  import('@/pages/policies-page').then(module => ({ 
    default: module.default 
  }))
);

export const CheckoutPage = lazyWithRetry(() => 
  import('@/pages/checkout-page').then(module => ({ 
    default: module.default 
  }))
);

export const BalancePaymentPage = lazyWithRetry(() =>
  import('@/pages/balance-payment-page').then(module => ({
    default: module.default
  }))
);

export const ExtensionPaymentPage = lazyWithRetry(() =>
  import('@/pages/extension-payment-page').then(module => ({
    default: module.default
  }))
);

export const MyStayPage = lazyWithRetry(() =>
  import('@/pages/my-stay-page').then(module => ({
    default: module.default
  }))
);

export const BookingConfirmationPage = lazyWithRetry(() =>
  import('@/pages/booking-confirmation-page').then(module => ({
    default: module.default
  }))
);

// Blog pages - lazy loaded for SEO-important content
export const BlogIndexPage = lazyWithRetry(() => 
  import('@/pages/blog/blog-index-page').then(module => ({ 
    default: module.default 
  }))
);

export const BlogPostPage = lazyWithRetry(() => 
  import('@/pages/blog/blog-post-page').then(module => ({ 
    default: module.default 
  }))
);

export const BlogCategoryPage = lazyWithRetry(() => 
  import('@/pages/blog/blog-category-page').then(module => ({ 
    default: module.default 
  }))
);

// Lazy load heavy components
export const LocationMap = lazyWithRetry(() => 
  import('@/components/property/location-map').then(module => ({ 
    default: module.LocationMap 
  }))
);

export const ReviewsSection = lazyWithRetry(() => 
  import('@/components/property/reviews-section')
);

export const ChatWidget = lazyWithRetry(() => 
  import('@/components/chat/chat-widget').then(module => ({ 
    default: module.ChatWidget 
  }))
);

// Helper function to create lazy components with custom loading states
export function createLazyComponent<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  LoadingComponent?: ComponentType
) {
  const LazyComponent = lazyWithRetry(importFn);
  
  return function LazyComponentWrapper(props: React.ComponentProps<T>) {
    return (
      <Suspense fallback={
        LoadingComponent ? <LoadingComponent /> : 
        <SkeletonCard hasImage={true} textLines={3} className="w-full" />
      }>
        <LazyComponent {...props} />
      </Suspense>
    );
  };
}

// Preload functions for performance
export const preloadAdminComponents = () => {
  // Preload admin components when user is authenticated
  const adminImports = [
    () => import('@/pages/admin/admin-dashboard'),
    () => import('@/pages/admin/admin-bookings'),
    () => import('@/pages/admin/admin-calendar'),
  ];
  
  adminImports.forEach(importFn => {
    importFn().catch(() => {
      // Fail silently for preloading
    });
  });
};

export const preloadSecondaryPages = () => {
  // Preload likely next pages
  const secondaryImports = [
    () => import('@/pages/contact-page'),
    () => import('@/pages/booking-page'),
  ];
  
  secondaryImports.forEach(importFn => {
    importFn().catch(() => {
      // Fail silently for preloading
    });
  });
};

// Custom hook for intelligent preloading
export function useIntelligentPreload() {
  const preloadOnHover = (componentName: string) => {
    const preloadMap: Record<string, () => Promise<any>> = {
      'contact': () => import('@/pages/contact-page'),
      'booking': () => import('@/pages/booking-page'),
      'checkout': () => import('@/pages/checkout-page'),
      'admin': () => import('@/pages/admin/admin-dashboard'),
      'reviews': () => import('@/components/property/reviews-section'),
      'map': () => import('@/components/property/location-map'),
    };
    
    const preloadFn = preloadMap[componentName];
    if (preloadFn) {
      preloadFn().catch(() => {
        // Fail silently
      });
    }
  };
  
  return { preloadOnHover };
}

// Intersection observer hook for lazy loading sections
export function useLazySection(threshold = 0.1) {
  const { elementRef, hasIntersected } = useIntersectionObserver({
    threshold,
    rootMargin: '100px', // Start loading before element is visible
    freezeOnceVisible: true,
  });
  
  return { ref: elementRef, shouldLoad: hasIntersected };
}

// Import the hook from existing file
import { useIntersectionObserver } from '@/hooks/use-intersection-observer';